/**
 * Helper utilities for web platform
 *
 * Contains utility functions that were previously from mobile-specific packages
 *
 * @module @kidslabs/module-flash-sale/utils/helpers
 */

import { useMemo } from 'react';
import { formatPrice } from './price.utils';

/**
 * Filter out null/undefined items from an array
 * Equivalent to removeUnavailableItems from mobile module-core
 *
 * @param items - Array that may contain null/undefined values
 * @returns Array with only defined values
 */
export const removeUnavailableItems = <T>(items: (T | null | undefined)[]): T[] => {
    return items.filter((item): item is T => !!item && item !== null);
};

import type { Observable } from '@legendapp/state';

/**
 * Type for disabled state observable
 * Used in gift selection components
 */
export type DisabledGiftItemObservable = Observable<boolean>;

/**
 * Check if code is running in browser
 */
export const isBrowser = typeof window !== 'undefined';

/**
 * Check if device is small (mobile breakpoint)
 * Web equivalent of isSmallDevice from mobile
 */
export const isSmallDevice = (): boolean => {
    if (!isBrowser) return false;
    return window.innerWidth < 768;
};

/**
 * Noop function for placeholder callbacks
 */
export const noop = (): void => {};

/**
 * Extract time string (HH:mm) from ISO date string
 *
 * @param isoString - ISO date string (e.g., "2025-01-24T14:30:00Z")
 * @returns Time string in HH:mm format (e.g., "14:30")
 */
export const extractTimeFromISOString = (isoString: string): string => {
    if (!isoString) return '';
    try {
        const date = new Date(isoString);
        if (isNaN(date.getTime())) return '';
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
    } catch {
        return '';
    }
};

/**
 * Check if a date is today
 *
 * @param dateString - Date string to check
 * @returns true if the date is today
 */
export const isToday = (dateString: string): boolean => {
    if (!dateString) return false;
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return false;
        const today = new Date();
        return (
            date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear()
        );
    } catch {
        return false;
    }
};

/**
 * Format a date to Vietnamese locale string.
 *
 * @param date - Date to format (Date object, string, or timestamp)
 * @param format - Optional format string (not used, for API compatibility)
 * @returns Formatted date string (DD/MM/YYYY)
 */
export const formatDate = (date: Date | string | number, format?: string): string => {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';

    if (format) {
        return d.toLocaleDateString('vi-VN');
    }

    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
};

/**
 * Calculate discount percentage.
 *
 * @param originalPrice - Original price
 * @param salePrice - Sale price
 * @returns Discount percentage (0-100)
 */
export const calculateDiscount = (originalPrice: number, salePrice: number): number => {
    if (originalPrice <= 0) return 0;
    return Math.round(((originalPrice - salePrice) / originalPrice) * 100);
};

/**
 * Hook for price calculations with memoization.
 * Provides formatPrice and calculateDiscount utilities.
 *
 * @param _product - Product object (optional, for future use)
 * @returns Price calculation utilities
 */
export const useCalculatePrices = () => {
    return useMemo(
        () => ({
            formatPrice: (amount: number) => formatPrice({ amount }),
            calculateDiscount,
            price: '' as string, // Placeholder for compatibility
        }),
        []
    );
};
